import { initPhoneInput } from '../phoneInput.js';
import { regionsForCountry } from '../taxRates.js';
import { withSubmitGuard } from '../submitGuard.js';

// ── US timezone whitelist (IANA IDs) ─────────────────────────────────────────
// Used to filter Intl.supportedValuesOf('timeZone') down to US-relevant entries.
// Intl generates all display labels + live offsets at runtime — no hardcoded strings.
const US_TZ_IDS = new Set([
  'Pacific/Pago_Pago',               // American Samoa   (UTC−11)
  'Pacific/Honolulu',                // Hawaii            (UTC−10, no DST)
  'America/Adak',                    // Aleutian Islands  (UTC−10/−9, has DST)
  'America/Anchorage',               // Alaska            (UTC−9/−8)
  'America/Juneau',
  'America/Sitka',
  'America/Metlakatla',              //                   (UTC−9, no DST)
  'America/Nome',
  'America/Yakutat',
  'America/Los_Angeles',             // Pacific           (UTC−8/−7)
  'America/Phoenix',                 // Arizona           (UTC−7, no DST)
  'America/Denver',                  // Mountain          (UTC−7/−6)
  'America/Boise',
  'America/Chicago',                 // Central           (UTC−6/−5)
  'America/Menominee',
  'America/Indiana/Knox',
  'America/Indiana/Tell_City',
  'America/North_Dakota/Center',
  'America/North_Dakota/New_Salem',
  'America/North_Dakota/Beulah',
  'America/New_York',                // Eastern           (UTC−5/−4)
  'America/Detroit',
  'America/Indiana/Indianapolis',
  'America/Indiana/Marengo',
  'America/Indiana/Petersburg',
  'America/Indiana/Vevay',
  'America/Indiana/Vincennes',
  'America/Indiana/Winamac',
  'America/Kentucky/Louisville',
  'America/Kentucky/Monticello',
  'America/Puerto_Rico',             // Atlantic PR       (UTC−4, no DST)
  'America/St_Thomas',               // US Virgin Islands (UTC−4)
  'Pacific/Guam',                    // Chamorro/Guam     (UTC+10, no DST)
  'Pacific/Saipan',                  // N. Mariana Is.    (UTC+10)
]);

function _buildTzOptions(selectedTz) {
  const now = new Date();

  // Pull every timezone the browser's IANA database knows, keep only US ones.
  // Falls back to the Set itself if supportedValuesOf isn't available (older browsers).
  const allTz = (typeof Intl.supportedValuesOf === 'function')
    ? Intl.supportedValuesOf('timeZone').filter(tz => US_TZ_IDS.has(tz))
    : [...US_TZ_IDS];

  const getPart = (tz, opts) =>
    new Intl.DateTimeFormat('en-US', { timeZone: tz, ...opts })
      .formatToParts(now).find(p => p.type === 'timeZoneName')?.value ?? '';

  return allTz
    .map(tz => {
      // Familiar abbreviation: PST, EST, CDT, HST …
      const abbr  = getPart(tz, { timeZoneName: 'short' });
      // Current UTC offset: GMT-8, GMT+10 …
      const offset = getPart(tz, { timeZoneName: 'shortOffset' });
      // Readable city / region from IANA ID tail
      const city  = tz.split('/').pop().replace(/_/g, ' ');
      // Numeric offset (ms) for west→east sort
      const dtStr = new Intl.DateTimeFormat('sv-SE', {
        timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
      }).format(now).replace(' ', 'T');
      const offsetMs = now.getTime() - new Date(dtStr + 'Z').getTime();

      return { tz, label: `${city} (${abbr}, ${offset})`, offsetMs, selected: tz === selectedTz };
    })
    .sort((a, b) => a.offsetMs - b.offsetMs);
}

class SettingsView {
  _modal           = document.getElementById("settingsModal");
  _phoneIti        = null;
  _profileOriginal = null;
  _businessOriginal = null;
  _openBtn = document.getElementById("settingsBtn");
  _closeBtn = document.getElementById("settingsCloseBtn");
  _showRemovedToggle = document.getElementById("showRemovedToggle");
  _printingToggle = document.getElementById("printingToggle");
  _confirmPrintToggle = document.getElementById("confirmPrintToggle");
  _showCashierToggle = document.getElementById("showCashierToggle");
  _orderTypeToggle = document.getElementById("orderTypeToggle");
  _invoiceNumberToggle = document.getElementById("invoiceNumberToggle");
  _ticketNumberToggle = document.getElementById("ticketNumberToggle");
  _kdsYellowMin = document.getElementById("kdsYellowMin");
  _kdsYellowSec = document.getElementById("kdsYellowSec");
  _kdsRedMin = document.getElementById("kdsRedMin");
  _kdsRedSec = document.getElementById("kdsRedSec");
  _kdsAutoMin = document.getElementById("kdsAutoMin");
  _kdsAutoSec = document.getElementById("kdsAutoSec");
  _btSection = document.getElementById("btPrinterSection");
  _btStatus = document.getElementById("btPrinterStatus");
  _btPaperWidth = document.getElementById("btPaperWidthToggle");
  _btDisconnectBtn = document.getElementById("btDisconnectBtn");
  _btTestBtn = document.getElementById("btTestBtn");

  // ── Tab navigation ────────────────────────────────────────────────────────────

  openWithRole(role) {
    if (role === 'Admin' && !this._phoneIti) {
      this._phoneIti = initPhoneInput('settingsBusinessPhoneNumber');
    }
    this._switchTab('profile');
    this.showBusinessSaveStatus(false, '');
  }

  _onTabView = null; // controller hook: fires with the tab name on every switch

  _switchTab(name) {
    document.querySelectorAll('.settings-nav-btn').forEach(btn => {
      btn.classList.toggle('active', btn.dataset.settingsTab === name);
    });
    const tabId = `settingsTab${name.charAt(0).toUpperCase() + name.slice(1)}`;
    document.querySelectorAll('.settings-tab').forEach(tab => {
      tab.classList.toggle('hidden', tab.id !== tabId);
    });
    this._onTabView?.(name);
  }

  _addHandlerNavTabs() {
    document.querySelector('.settings-nav')?.addEventListener('click', e => {
      const btn = e.target.closest('.settings-nav-btn[data-settings-tab]');
      if (!btn) return;
      this._switchTab(btn.dataset.settingsTab);
      this._closeMobileNav();
    });
  }

  // ── Mobile nav drawer ────────────────────────────────────────────────────────
  // Below the 640px breakpoint the nav is an off-canvas drawer instead of a
  // static sidebar - opened via the hamburger button, closed by the backdrop,
  // the × close button, or picking a tab.

  _openMobileNav() {
    document.getElementById('settingsNav')?.classList.add('settings-nav--open');
    document.getElementById('settingsNavBackdrop')?.classList.add('settings-nav-backdrop--visible');
    document.getElementById('settingsMobileMenuBtn')?.setAttribute('aria-expanded', 'true');
  }

  _closeMobileNav() {
    document.getElementById('settingsNav')?.classList.remove('settings-nav--open');
    document.getElementById('settingsNavBackdrop')?.classList.remove('settings-nav-backdrop--visible');
    document.getElementById('settingsMobileMenuBtn')?.setAttribute('aria-expanded', 'false');
  }

  _addHandlerMobileNav() {
    const btn = document.getElementById('settingsMobileMenuBtn');
    btn?.addEventListener('click', () => {
      const open = btn.getAttribute('aria-expanded') === 'true';
      if (open) this._closeMobileNav(); else this._openMobileNav();
    });
    document.getElementById('settingsNavBackdrop')?.addEventListener('click', () => this._closeMobileNav());
  }

  // ── Business info ─────────────────────────────────────────────────────────────

  syncBusinessInfo({ name, email, phone, timezone, address, city, state, zip, country, isOwner }) {
    this._businessCountry = country ?? '';
    const n  = document.getElementById('settingsBusinessName');
    const e  = document.getElementById('settingsBusinessEmail');
    const a  = document.getElementById('settingsBusinessAddress');
    const c  = document.getElementById('settingsBusinessCity');
    const st = document.getElementById('settingsBusinessState');
    const z  = document.getElementById('settingsBusinessZip');
    if (n)  { n.value  = name    ?? ''; n.setAttribute('readonly',  ''); }
    if (e)  { e.value  = email   ?? ''; e.setAttribute('readonly',  ''); }
    if (a)  { a.value  = address ?? ''; a.setAttribute('readonly',  ''); }
    if (c)  { c.value  = city    ?? ''; c.setAttribute('readonly',  ''); }
    if (st) { st.value = state   ?? ''; st.setAttribute('readonly', ''); }
    if (z)  { z.value  = zip     ?? ''; z.setAttribute('readonly',  ''); }
    // View (readonly) mode always shows the text field; the dropdown is for editing.
    this._showStateControl(false);
    if (this._phoneIti) {
      this._phoneIti.setNumber(phone ?? '');
      document.getElementById('settingsBusinessPhoneNumber')?.setAttribute('readonly', '');
      document.getElementById('settingsBusinessPhoneNumber')?.closest('.iti')?.classList.add('iti--view');
    }
    document.getElementById('businessSaveRow')?.classList.add('hidden');
    document.getElementById('businessEditBtn')?.classList.remove('hidden');
    document.getElementById('settingsTimezone') && (document.getElementById('settingsTimezone').disabled = true);
    this._businessOriginal = null;
    this._syncFooterIdle();

    // Timezone field — only shown to the business owner
    const tzField = document.getElementById('settingsTimezoneField');
    if (tzField) {
      tzField.classList.toggle('hidden', !isOwner);
      if (isOwner) {
        const tzSel = document.getElementById('settingsTimezone');
        if (tzSel) {
          // Default to current browser timezone if none saved
          const activeTz = timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
          const options = _buildTzOptions(activeTz);
          tzSel.innerHTML = options
            .map(o => `<option value="${o.tz}"${o.selected ? ' selected' : ''}>${o.label}</option>`)
            .join('');
          // Fallback: if saved tz not in list, prepend it
          if (!options.some(o => o.selected) && activeTz) {
            tzSel.insertAdjacentHTML('afterbegin',
              `<option value="${activeTz}" selected>${activeTz}</option>`);
          }
        }
      }
    }
  }

  // ── Shared footer state machine ─────────────────────────────────────────────
  // Exactly one of idle / profile / business / settings shows at a time.
  // `_pendingSettingsDirty` tracks an unsaved POS/KDS toggle change made while
  // Profile or Business had priority (a deliberate, already-in-progress edit
  // should never be silently bumped off the footer by an ambient toggle flip);
  // it resurfaces once that edit finishes (saved or discarded).
  _pendingSettingsDirty = false;
  _discardSettingsDraftCallback = null;

  _setFooterState(state) {
    document.getElementById('settingsFooterIdle')?.classList.toggle('hidden', state !== 'idle');
    document.getElementById('profileSaveRow')?.classList.toggle('hidden', state !== 'profile');
    document.getElementById('businessSaveRow')?.classList.toggle('hidden', state !== 'business');
    document.getElementById('settingsDraftRow')?.classList.toggle('hidden', state !== 'settings');
  }

  _currentEditingSection() {
    const p = document.getElementById('profileSaveRow');
    const b = document.getElementById('businessSaveRow');
    if (p && !p.classList.contains('hidden')) return 'profile';
    if (b && !b.classList.contains('hidden')) return 'business';
    return null;
  }

  // Kept for compatibility with call sites that just want to fall back to
  // idle/pending-settings after a Profile or Business edit finishes.
  _syncFooterIdle() {
    if (this._currentEditingSection()) return;
    this._setFooterState(this._pendingSettingsDirty ? 'settings' : 'idle');
  }

  // A POS/KDS toggle or threshold changed. Doesn't steal the footer from an
  // in-progress Profile/Business edit - just remembers the flag.
  showSettingsDirty() {
    this._pendingSettingsDirty = true;
    if (!this._currentEditingSection()) this._setFooterState('settings');
  }

  hideSettingsDirty() {
    this._pendingSettingsDirty = false;
    if (!this._currentEditingSection()) this._setFooterState('idle');
  }

  // Registers the controller callback that reverts a pending settings draft
  // when the modal is force-closed with unsaved toggle changes.
  _addHandlerDiscardSettingsDraftOnClose(handler) {
    this._discardSettingsDraftCallback = handler;
  }

  _addHandlerSaveSettingsDraft(handler) {
    document.getElementById('saveSettingsDraftBtn')?.addEventListener('click', (e) => {
      withSubmitGuard(e.currentTarget, () => handler(), { busyText: 'Saving…' });
    });
  }

  _addHandlerDiscardSettingsDraft(handler) {
    document.getElementById('discardSettingsDraftBtn')?.addEventListener('click', () => handler());
  }

  enterBusinessEditMode() {
    if (!document.getElementById('profileSaveRow')?.classList.contains('hidden')) {
      this.exitProfileEditMode(true);
    }
    const ids = ['settingsBusinessName','settingsBusinessAddress','settingsBusinessCity',
                 'settingsBusinessState','settingsBusinessZip','settingsBusinessEmail'];
    this._businessOriginal = this._getBusinessFormData();
    ids.forEach(id => document.getElementById(id)?.removeAttribute('readonly'));
    document.getElementById('settingsBusinessPhoneNumber')?.removeAttribute('readonly');
    document.getElementById('settingsBusinessPhoneNumber')?.closest('.iti')?.classList.remove('iti--view');
    const tzSel = document.getElementById('settingsTimezone');
    if (tzSel) tzSel.disabled = false;
    this._setFooterState('business');
    document.getElementById('businessEditBtn').classList.add('hidden');
    const statusEl = document.getElementById('businessSaveStatus');
    if (statusEl) { statusEl.textContent = 'Unsaved changes'; statusEl.className = 'settings-save-status'; }
    // For US/CA, edit the state via a constrained dropdown (like onboarding).
    this._showStateControl(true);
    document.getElementById('settingsBusinessName')?.focus();
  }

  exitBusinessEditMode(restore = false) {
    const ids = ['settingsBusinessName','settingsBusinessAddress','settingsBusinessCity',
                 'settingsBusinessState','settingsBusinessZip','settingsBusinessEmail'];
    if (restore && this._businessOriginal) {
      const d = this._businessOriginal;
      document.getElementById('settingsBusinessName').value    = d.name    ?? '';
      document.getElementById('settingsBusinessAddress').value = d.address ?? '';
      document.getElementById('settingsBusinessCity').value    = d.city    ?? '';
      document.getElementById('settingsBusinessState').value   = d.state   ?? '';
      document.getElementById('settingsBusinessZip').value     = d.zip     ?? '';
      document.getElementById('settingsBusinessEmail').value   = d.email   ?? '';
      if (this._phoneIti) this._phoneIti.setNumber(d.phone ?? '');
    } else {
      // Saved (not cancelled): mirror the chosen dropdown value back into the text
      // field so the readonly view shows the new state after the dropdown is hidden.
      const sel = document.getElementById('settingsBusinessStateSelect');
      if (sel && !sel.classList.contains('hidden') && sel.value) {
        document.getElementById('settingsBusinessState').value = sel.selectedOptions[0].textContent.trim();
      }
    }
    this._showStateControl(false);
    ids.forEach(id => document.getElementById(id)?.setAttribute('readonly', ''));
    document.getElementById('settingsBusinessPhoneNumber')?.setAttribute('readonly', '');
    document.getElementById('settingsBusinessPhoneNumber')?.closest('.iti')?.classList.add('iti--view');
    const tzSel = document.getElementById('settingsTimezone');
    if (tzSel) tzSel.disabled = true;
    document.getElementById('businessSaveRow').classList.add('hidden');
    document.getElementById('businessEditBtn').classList.remove('hidden');
    this.showBusinessSaveStatus(false, '');
    this._businessOriginal = null;
    this._syncFooterIdle();
  }

  _getBusinessFormData() {
    const phone = this._phoneIti ? this._phoneIti.getNumber() : '';
    const tzField = document.getElementById('settingsTimezoneField');
    const timezone = (!tzField || tzField.classList.contains('hidden'))
      ? null
      : (document.getElementById('settingsTimezone')?.value || null);
    return {
      name:    document.getElementById('settingsBusinessName')?.value.trim()    ?? '',
      email:   document.getElementById('settingsBusinessEmail')?.value.trim()   ?? '',
      phone,
      timezone,
      address: document.getElementById('settingsBusinessAddress')?.value.trim() ?? '',
      city:    document.getElementById('settingsBusinessCity')?.value.trim()    ?? '',
      state:   this._stateValue(),
      zip:     document.getElementById('settingsBusinessZip')?.value.trim()     ?? '',
    };
  }

  // The state value, reading the dropdown when it's the active control (US/CA) or the
  // free-text field otherwise. Returns the full region name (consistent with onboarding).
  _stateValue() {
    const sel = document.getElementById('settingsBusinessStateSelect');
    if (sel && !sel.classList.contains('hidden')) {
      return sel.value ? sel.selectedOptions[0]?.textContent.trim() ?? '' : '';
    }
    return document.getElementById('settingsBusinessState')?.value.trim() ?? '';
  }

  _populateBusinessRegions(country, regions) {
    const sel = document.getElementById('settingsBusinessStateSelect');
    if (!sel) return;
    const cc = (country ?? '').toUpperCase();
    if (sel.dataset.filledFor === cc) return;
    sel.innerHTML = '<option value="">Select one</option>';
    regions.forEach(({ code, name }) => {
      const o = document.createElement('option');
      o.value = code;
      o.textContent = name;
      sel.appendChild(o);
    });
    sel.dataset.filledFor = cc;
  }

  // editing=true + a US/CA country -> show the constrained dropdown (preselected to the
  // current state); otherwise show the free-text field. View mode always uses text.
  _showStateControl(editing) {
    const input = document.getElementById('settingsBusinessState');
    const sel   = document.getElementById('settingsBusinessStateSelect');
    if (!input || !sel) return;
    const regions = editing ? regionsForCountry(this._businessCountry) : null;
    if (regions) {
      this._populateBusinessRegions(this._businessCountry, regions);
      const cur = input.value.trim();
      const match = regions.find(
        (r) => r.name.toLowerCase() === cur.toLowerCase() || r.code === cur.toUpperCase()
      );
      sel.value = match ? match.code : '';
      sel.classList.remove('hidden');
      input.classList.add('hidden');
    } else {
      sel.classList.add('hidden');
      input.classList.remove('hidden');
    }
  }

  _addHandlerSaveBusinessInfo(handler) {
    document.getElementById('saveBusinessInfoBtn')?.addEventListener('click', (e) => {
      const btn = e.currentTarget;
      const data = this._getBusinessFormData();
      if (!data.name) { this.showBusinessSaveStatus(false, 'Business name is required.'); return; }
      withSubmitGuard(btn, () => handler(data), { busyText: 'Saving…' });
    });
  }

  // Reminder shown after a business address change so the owner reviews their tax rate.
  showTaxReminder(message) {
    const modal = document.getElementById('taxReminderModal');
    const text  = document.getElementById('taxReminderText');
    if (text && message) text.textContent = message;
    modal?.classList.remove('hidden');
  }

  _addHandlerTaxReminderClose() {
    const modal = document.getElementById('taxReminderModal');
    document.getElementById('taxReminderCloseBtn')?.addEventListener('click', () => {
      modal?.classList.add('hidden');
    });
    modal?.addEventListener('click', (e) => {
      if (e.target === modal) modal.classList.add('hidden');
    });
  }


  showBusinessSaveStatus(success, msg) {
    const el = document.getElementById('businessSaveStatus');
    if (!el) return;
    el.textContent = msg;
    el.className = `settings-save-status${success ? ' settings-save-status--ok' : msg ? ' settings-save-status--err' : ''}`;
    if (success) setTimeout(() => { el.textContent = ''; el.className = 'settings-save-status'; }, 3000);
  }

  // ── Profile info ──────────────────────────────────────────────────────────────

  syncProfileInfo({ firstName, lastName, email }) {
    const f = document.getElementById('settingsProfileFirstName');
    const l = document.getElementById('settingsProfileLastName');
    const e = document.getElementById('settingsProfileEmail');
    if (f) { f.value = firstName ?? ''; f.setAttribute('readonly', ''); }
    if (l) { l.value = lastName  ?? ''; l.setAttribute('readonly', ''); }
    if (e) { e.value = email ?? ''; } // always readonly - login identity, not editable here
    document.getElementById('profileSaveRow')?.classList.add('hidden');
    document.getElementById('profileEditBtn')?.classList.remove('hidden');
    this._profileOriginal = null;
    this._syncFooterIdle();
  }

  enterProfileEditMode() {
    if (!document.getElementById('businessSaveRow')?.classList.contains('hidden')) {
      this.exitBusinessEditMode(true);
    }
    const f = document.getElementById('settingsProfileFirstName');
    const l = document.getElementById('settingsProfileLastName');
    this._profileOriginal = { firstName: f?.value ?? '', lastName: l?.value ?? '' };
    f?.removeAttribute('readonly');
    l?.removeAttribute('readonly');
    this._setFooterState('profile');
    document.getElementById('profileEditBtn').classList.add('hidden');
    const statusEl = document.getElementById('profileSaveStatus');
    if (statusEl) { statusEl.textContent = 'Unsaved changes'; statusEl.className = 'settings-save-status'; }
    f?.focus();
  }

  exitProfileEditMode(restore = false) {
    const f = document.getElementById('settingsProfileFirstName');
    const l = document.getElementById('settingsProfileLastName');
    if (restore && this._profileOriginal) {
      if (f) f.value = this._profileOriginal.firstName;
      if (l) l.value = this._profileOriginal.lastName;
    }
    f?.setAttribute('readonly', '');
    l?.setAttribute('readonly', '');
    document.getElementById('profileSaveRow').classList.add('hidden');
    document.getElementById('profileEditBtn').classList.remove('hidden');
    this.showProfileSaveStatus(false, '');
    this._profileOriginal = null;
    this._syncFooterIdle();
  }

  showProfileSaveStatus(success, msg) {
    const el = document.getElementById('profileSaveStatus');
    if (!el) return;
    el.textContent = msg;
    el.className = `settings-save-status${success ? ' settings-save-status--ok' : msg ? ' settings-save-status--err' : ''}`;
    if (success) setTimeout(() => { el.textContent = ''; el.className = 'settings-save-status'; }, 3000);
  }

  // ── OTP verification modal ────────────────────────────────────────────────────

  showOTPModal(email) {
    const hint = document.getElementById('settingsOTPHint');
    if (hint) hint.textContent = `A 6-digit code was sent to ${email}.`;
    document.getElementById('settingsOTPError').textContent = '';
    document.getElementById('settingsOTPInput').value = '';
    document.getElementById('settingsOTPVerifyBtn').disabled = false;
    document.getElementById('settingsOTPVerifyBtn').textContent = 'Verify & Save';
    document.getElementById('settingsOTPModal').classList.remove('hidden');
    setTimeout(() => document.getElementById('settingsOTPInput')?.focus(), 100);
  }

  hideOTPModal() {
    document.getElementById('settingsOTPModal').classList.add('hidden');
  }

  showOTPModalError(msg) {
    document.getElementById('settingsOTPError').textContent = msg;
    const btn = document.getElementById('settingsOTPVerifyBtn');
    if (btn) { btn.disabled = false; btn.textContent = 'Verify & Save'; }
  }

  _addHandlerEditProfile(handler) {
    document.getElementById('profileEditBtn')?.addEventListener('click', () => handler());
  }

  _addHandlerCancelProfile(handler) {
    document.getElementById('cancelProfileBtn')?.addEventListener('click', () => handler());
  }

  _addHandlerEditBusiness(handler) {
    document.getElementById('businessEditBtn')?.addEventListener('click', () => handler());
  }

  _addHandlerCancelBusiness(handler) {
    document.getElementById('cancelBusinessBtn')?.addEventListener('click', () => handler());
  }

  _addHandlerSaveProfile(handler) {
    document.getElementById('saveProfileBtn')?.addEventListener('click', (e) => {
      const btn = e.currentTarget;
      const firstName = document.getElementById('settingsProfileFirstName')?.value.trim();
      const lastName  = document.getElementById('settingsProfileLastName')?.value.trim();
      if (!firstName) { this.showProfileSaveStatus(false, 'First name is required.'); return; }
      if (!lastName)  { this.showProfileSaveStatus(false, 'Last name is required.'); return; }
      withSubmitGuard(btn, () => handler({ firstName, lastName }), { busyText: 'Saving…' });
    });
  }

  _addHandlerVerifyOTP(handler) {
    document.getElementById('settingsOTPVerifyBtn')?.addEventListener('click', (e) => {
      const btn = e.currentTarget;
      const token = document.getElementById('settingsOTPInput')?.value.trim();
      if (!token || !/^\d{6}$/.test(token)) {
        document.getElementById('settingsOTPError').textContent = 'Please enter the 6-digit code.';
        return;
      }
      withSubmitGuard(btn, () => handler(token), { busyText: 'Verifying…' });
    });
  }

  _addHandlerCancelOTP(handler) {
    document.getElementById('settingsOTPCancelBtn')?.addEventListener('click', () => handler());
  }

  // ── Two-Factor Authentication ────────────────────────────────────────────────

  // Toggle the Security section between the off (enable) and on (disable) states.
  render2FA(enabled) {
    document.getElementById('twoFAStatus')?.replaceChildren(enabled ? 'On' : 'Off');
    document.getElementById('twoFAStatus')?.classList.toggle('settings-2fa-badge--on', !!enabled);
    document.getElementById('twoFAOffView')?.classList.toggle('hidden', !!enabled);
    document.getElementById('twoFAOnView')?.classList.toggle('hidden', !enabled);
  }

  show2FAEnroll({ qr, secret }) {
    const img = document.getElementById('mfaQrImg');
    if (img) img.src = qr;
    const code = document.getElementById('mfaSecret');
    if (code) code.textContent = secret;
    const input = document.getElementById('mfaEnrollInput');
    if (input) input.value = '';
    document.getElementById('mfaEnrollError').textContent = '';
    document.getElementById('mfaEnrollModal')?.classList.remove('hidden');
    input?.focus();
  }

  hide2FAEnroll() {
    document.getElementById('mfaEnrollModal')?.classList.add('hidden');
  }

  set2FAEnrollError(msg) {
    const el = document.getElementById('mfaEnrollError');
    if (el) el.textContent = msg;
  }

  // Keep the enroll modal up with a spinner while recovery codes are generated (a
  // server round-trip after the code verifies) so the user knows setup isn't done
  // and doesn't navigate away before the codes modal appears.
  set2FAEnrollBusy(text) {
    const btn = document.getElementById('mfaEnrollVerifyBtn');
    if (btn) { btn.disabled = true; btn.innerHTML = `<span class="btn-spinner"></span>${text}`; }
    const err = document.getElementById('mfaEnrollError');
    if (err) err.textContent = '';
  }

  _addHandler2FAEnable(handler) {
    document.getElementById('enable2FABtn')?.addEventListener('click', (e) =>
      withSubmitGuard(e.currentTarget, () => handler(), { busyText: 'Starting…' }));
  }

  _addHandler2FADisable(handler) {
    document.getElementById('disable2FABtn')?.addEventListener('click', (e) =>
      withSubmitGuard(e.currentTarget, () => handler()));
  }

  _addHandler2FAVerifyEnroll(handler) {
    const verify = (btn) => {
      const code = document.getElementById('mfaEnrollInput')?.value.trim();
      if (!code || !/^\d{6}$/.test(code)) { this.set2FAEnrollError('Enter the 6-digit code.'); return; }
      withSubmitGuard(btn, () => handler(code), { busyText: 'Verifying…' });
    };
    document.getElementById('mfaEnrollVerifyBtn')?.addEventListener('click', (e) => verify(e.currentTarget));
    document.getElementById('mfaEnrollInput')?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') verify(document.getElementById('mfaEnrollVerifyBtn'));
    });
  }

  _addHandler2FACancelEnroll() {
    document.getElementById('mfaEnrollCancelBtn')?.addEventListener('click', () => this.hide2FAEnroll());
  }

  // ── Recovery codes (shown once after enrollment) ──────────────────────────────

  showRecoveryCodes(codes) {
    this._recoveryCodes = codes;
    const list = document.getElementById('mfaCodesList');
    if (list) list.innerHTML = codes.map((c) => `<code>${c}</code>`).join('');
    // Done stays clickable; clicking it without ticking the box shows a reminder
    // toast (a disabled button gave no feedback about why nothing happened).
    const ack = document.getElementById('mfaCodesAck');
    if (ack) ack.checked = false;
    document.getElementById('mfaCodesModal')?.classList.remove('hidden');
  }

  hideRecoveryCodes() {
    document.getElementById('mfaCodesModal')?.classList.add('hidden');
  }

  _addHandler2FACodes(onDone, onUnacked) {
    const codesText = () => (this._recoveryCodes ?? []).join('\n');

    document.getElementById('mfaCodesCopyBtn')?.addEventListener('click', (e) => {
      navigator.clipboard?.writeText(codesText());
      const btn = e.currentTarget;
      const prev = btn.textContent;
      btn.textContent = 'Copied!';
      setTimeout(() => { btn.textContent = prev; }, 1500);
    });

    document.getElementById('mfaCodesDownloadBtn')?.addEventListener('click', () => {
      const blob = new Blob(
        [`Pointbunny two-factor recovery codes\nKeep these somewhere safe. Each works once.\n\n${codesText()}\n`],
        { type: 'text/plain' },
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'pointbunny-recovery-codes.txt';
      a.click();
      URL.revokeObjectURL(url);
    });

    document.getElementById('mfaCodesDoneBtn')?.addEventListener('click', () => {
      const ack = document.getElementById('mfaCodesAck');
      if (!ack?.checked) { onUnacked?.(); return; } // remind, don't proceed
      this.hideRecoveryCodes();
      onDone?.();
    });
  }

  // ── Open / Close ─────────────────────────────────────────────────────────────

  // Reveals the modal. Deep-links (e.g. the Getting Started "Set up your
  // receipt" step) call this + the controller's open handler themselves,
  // since only the gear button's listener used to unhide the modal.
  open() {
    this._modal.classList.remove("hidden");
  }

  _addHandlerOpen(handler) {
    this._openBtn.addEventListener("click", () => {
      this.open();
      handler();
    });
  }

  _addHandlerClose() {
    this._closeBtn.addEventListener("click", () => this._requestClose());
    this._modal.addEventListener("click", (e) => {
      if (e.target === this._modal) this._requestClose();
    });
  }

  // onClosed (optional) only fires once the modal actually closes - i.e. not
  // if the user cancels an unsaved-changes prompt. Used by callers that want
  // to open something else right after Settings closes (e.g. "Manage adjustments").
  close(onClosed) {
    this._requestClose(onClosed);
  }

  // True while Profile/Business is mid-edit, or a POS/KDS toggle changed but
  // hasn't been saved or discarded yet.
  _hasUnsavedEdits() {
    return this._currentEditingSection() !== null || this._pendingSettingsDirty;
  }

  // Warn before closing with unsaved edits so a stray backdrop click or the
  // × button can't silently throw away a half-finished name/address change.
  _requestClose(onClosed) {
    if (!this._hasUnsavedEdits()) {
      this._close();
      onClosed?.();
      return;
    }
    this._showConfirm({
      message: 'You have unsaved changes.',
      note: 'If you leave now, your changes will be discarded.',
      confirmLabel: 'Discard & Close',
      cancelLabel: 'Keep Editing',
      onConfirm: () => { this._close(); onClosed?.(); },
    });
  }

  _close() {
    this.hideOTPModal();
    this.exitProfileEditMode(true);
    this.exitBusinessEditMode(true);
    if (this._pendingSettingsDirty) {
      this._discardSettingsDraftCallback?.();
    }
    this._closeMobileNav();
    const inner = this._modal.querySelector(".modal-container");
    if (inner) inner.classList.add("modal-exiting");
    setTimeout(() => {
      if (inner) inner.classList.remove("modal-exiting");
      this._modal.classList.add("hidden");
    }, 220);
  }

  // ── Handlers ──────────────────────────────────────────────────────────────────

  _addHandlerShowRemoved(handler) {
    this._showRemovedToggle.addEventListener("change", () => {
      handler(this._showRemovedToggle.checked);
    });
  }

  syncShowRemovedToggle(value) {
    this._showRemovedToggle.checked = value;
  }

  _addHandlerTogglePrinting(handler) {
    this._printingToggle.addEventListener("change", () => {
      handler(this._printingToggle.checked);
    });
  }

  syncPrintingToggle(value) {
    this._printingToggle.checked = value;
  }

  _addHandlerToggleConfirmPrint(handler) {
    this._confirmPrintToggle.addEventListener("change", () => {
      handler(this._confirmPrintToggle.checked);
    });
  }

  syncConfirmPrintToggle(value) {
    this._confirmPrintToggle.checked = value;
  }

  _addHandlerToggleShowCashier(handler) {
    this._showCashierToggle.addEventListener("change", () => {
      handler(this._showCashierToggle.checked);
    });
  }

  syncShowCashierToggle(value) {
    this._showCashierToggle.checked = value;
  }

  _addHandlerToggleHideStats(handler) {
    document.getElementById("hideStatsToggle")?.addEventListener("change", (e) => {
      handler(e.target.checked);
    });
  }

  syncHideStatsToggle(value) {
    const el = document.getElementById("hideStatsToggle");
    if (el) el.checked = value === true;
  }

  // ── Bluetooth thermal printer ─────────────────────────────────────────────
  _addHandlerBtDisconnect(handler) {
    this._btDisconnectBtn?.addEventListener('click', () => handler());
  }
  _addHandlerBtTest(handler) {
    this._btTestBtn?.addEventListener('click', () => handler());
  }
  // Branded segmented pill (mirrors .pos-visual-toggle) instead of a plain
  // <select>. Clicking moves the thumb + active state immediately (same
  // "changes apply live" feel as the other Settings toggles) - the actual
  // save is staged/deferred by the caller via the shared footer.
  _addHandlerBtPaperWidth(handler) {
    this._btPaperWidth?.querySelectorAll('.bt-paper-toggle-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        const value = btn.dataset.value;
        this.syncBtPaperWidth(value);
        handler(value);
      });
    });
  }

  syncBtPaperWidth(value) {
    if (!this._btPaperWidth) return;
    const v = String(Number(value) === 58 ? 58 : 80);
    this._btPaperWidth.dataset.width = v;
    this._btPaperWidth.querySelectorAll('.bt-paper-toggle-btn').forEach((btn) => {
      btn.classList.toggle('bt-paper-toggle-btn--active', btn.dataset.value === v);
    });
  }

  // Deliberately does NOT track/display a live "connected" status - Web
  // Bluetooth's own connection reporting proved unreliable to mirror (idle
  // auto-disconnects, hung reconnect probes on Windows). Test / Disconnect
  // are always available and report success or failure via toast; print()
  // handles (re)connecting to the already-paired printer on its own.
  syncBtPrinter({ supported }) {
    if (!this._btStatus) return;
    this._btStatus.classList.remove('bt-status--unsupported');
    this._btSection?.classList.toggle('bt-unsupported', !supported);

    if (!supported) {
      this._btStatus.textContent =
        "This browser/device does not support Bluetooth printing (iOS Safari and Firefox can't). Receipts use normal printing here.";
      this._btStatus.classList.add('bt-status--unsupported');
      if (this._btTestBtn) this._btTestBtn.disabled = true;
      if (this._btDisconnectBtn) this._btDisconnectBtn.disabled = true;
      return;
    }

    this._btStatus.textContent =
      "Your Bluetooth receipt printer connects automatically. Receipts print here whenever it's reachable.";
    if (this._btTestBtn) this._btTestBtn.disabled = false;
    if (this._btDisconnectBtn) this._btDisconnectBtn.disabled = false;
  }

  _addHandlerToggleOrderType(handler) {
    this._orderTypeToggle.addEventListener('change', () => handler(this._orderTypeToggle.checked));
  }

  syncOrderTypeToggle(value) {
    this._orderTypeToggle.checked = value;
  }

  // Lightweight confirm dialog. SettingsView does not extend View, so it cannot
  // use View.showConfirmModal - this mirrors the same `.confirm-overlay` markup so
  // styling is identical.
  _showConfirm({ message, note, confirmLabel = 'Confirm', cancelLabel = 'Cancel', onConfirm, onCancel }) {
    if (document.querySelector('.confirm-overlay')) return;
    const el = document.createElement('div');
    el.className = 'confirm-overlay';
    el.innerHTML = `
      <div class="confirm-card">
        <p class="confirm-msg">${message}</p>
        ${note ? `<p class="confirm-note">${note}</p>` : ''}
        <div class="confirm-actions">
          <button class="btn confirm-cancel-btn" type="button">${cancelLabel}</button>
          <button class="btn primary confirm-ok-btn" type="button">${confirmLabel}</button>
        </div>
      </div>`;
    document.body.appendChild(el);
    el.querySelector('.confirm-ok-btn').addEventListener('click', () => { el.remove(); onConfirm?.(); });
    el.querySelector('.confirm-cancel-btn').addEventListener('click', () => { el.remove(); onCancel?.(); });
    el.addEventListener('click', (e) => { if (e.target === el) { el.remove(); onCancel?.(); } });
  }

  // Turning the invoice number OFF is allowed, but warn first - many countries
  // legally require a sequential invoice number on receipts. Cancelling the
  // warning reverts the toggle. Turning it back on needs no confirmation.
  _addHandlerToggleInvoiceNumber(handler) {
    this._invoiceNumberToggle.addEventListener('change', () => {
      if (this._invoiceNumberToggle.checked) {
        handler(true);
        return;
      }
      this._showConfirm({
        message: 'Hide the invoice number on receipts?',
        note: 'Some countries (VAT/GST regions like the UK, EU, Australia and the Philippines) legally require a sequential invoice number on every receipt. The number is still recorded internally either way - this only hides it from the printed receipt. This is your choice.',
        confirmLabel: 'Yes, hide it',
        cancelLabel: 'Keep it on',
        onConfirm: () => handler(false),
        onCancel: () => { this._invoiceNumberToggle.checked = true; },
      });
    });
  }

  syncInvoiceNumberToggle(value) {
    this._invoiceNumberToggle.checked = value;
  }

  _addHandlerToggleTicketNumber(handler) {
    this._ticketNumberToggle.addEventListener('change', () => handler(this._ticketNumberToggle.checked));
  }

  syncTicketNumberToggle(value) {
    this._ticketNumberToggle.checked = value;
  }

  // ── Display window size ───────────────────────────────────────────────────────

  _sizeToOption(size) {
    const key = `${size.width}x${size.height}`;
    return ['1024x768', '1280x720', '1920x1080'].includes(key) ? key : 'custom';
  }

  // Decorative dims label under each screen-size preview thumbnail.
  _updatePreviewDims(id, size) {
    const el = document.getElementById(id);
    if (el) el.textContent = `${size.width}×${size.height}`;
  }

  syncDisplaySizes(kdsSize, cfdSize) {
    const kdsSelect = document.getElementById('kdsWindowSizeSelect');
    const cfdSelect = document.getElementById('cfdWindowSizeSelect');

    const kdsOpt = this._sizeToOption(kdsSize);
    kdsSelect.value = kdsOpt;
    if (kdsOpt === 'custom') {
      document.getElementById('kdsCustomSize').classList.remove('hidden');
      document.getElementById('kdsCustomW').value = kdsSize.width;
      document.getElementById('kdsCustomH').value = kdsSize.height;
    }
    this._updatePreviewDims('kdsPreviewDims', kdsSize);

    const cfdOpt = this._sizeToOption(cfdSize);
    cfdSelect.value = cfdOpt;
    if (cfdOpt === 'custom') {
      document.getElementById('cfdCustomSize').classList.remove('hidden');
      document.getElementById('cfdCustomW').value = cfdSize.width;
      document.getElementById('cfdCustomH').value = cfdSize.height;
    }
    this._updatePreviewDims('cfdPreviewDims', cfdSize);

    // Sync image previews from their last-SAVED value (localStorage cache) -
    // reverts a discarded upload/removal back to what's actually persisted.
    this._syncImagePreview('cfdAd', localStorage.getItem('pointbunny_cfd_ad'));
    this._syncImagePreview('pickupAd', localStorage.getItem('pointbunny_pickup_ad'));
    this._syncImagePreview('cfdQr', localStorage.getItem('pointbunny_cfd_qr'));
  }

  // Shared by the three Displays image cards (CFD ad, pickup-screen ad,
  // payment QR) - `prefix` matches their id naming (cfdAdPreview/Img/RemoveBtn
  // etc). Handles both directions: show a url, or hide when null/falsy -
  // needed so discarding a staged upload actually reverts the preview, not
  // just discarding a staged removal.
  _syncImagePreview(prefix, url) {
    const preview = document.getElementById(`${prefix}Preview`);
    const img = document.getElementById(`${prefix}PreviewImg`);
    const removeBtn = document.getElementById(`${prefix}RemoveBtn`);
    if (!preview || !img || !removeBtn) return;
    if (url) {
      preview.classList.remove('hidden');
      img.src = url;
      removeBtn.classList.remove('hidden');
    } else {
      preview.classList.add('hidden');
      img.src = '';
      removeBtn.classList.add('hidden');
    }
  }

  _addHandlerDisplaySizes(kdsHandler, cfdHandler) {
    const kdsSelect  = document.getElementById('kdsWindowSizeSelect');
    const cfdSelect  = document.getElementById('cfdWindowSizeSelect');
    const kdsCustom  = document.getElementById('kdsCustomSize');
    const cfdCustom  = document.getElementById('cfdCustomSize');

    const readSize = (select, customEl) => {
      if (select.value === 'custom') {
        const w = parseInt(customEl.querySelector('[id$="CustomW"]').value) || 1920;
        const h = parseInt(customEl.querySelector('[id$="CustomH"]').value) || 1080;
        return { width: w, height: h };
      }
      const [w, h] = select.value.split('x').map(Number);
      return { width: w, height: h };
    };

    kdsSelect.addEventListener('change', () => {
      kdsCustom.classList.toggle('hidden', kdsSelect.value !== 'custom');
      this._updatePreviewDims('kdsPreviewDims', readSize(kdsSelect, kdsCustom));
      if (kdsSelect.value !== 'custom') kdsHandler(readSize(kdsSelect, kdsCustom));
    });

    cfdSelect.addEventListener('change', () => {
      cfdCustom.classList.toggle('hidden', cfdSelect.value !== 'custom');
      this._updatePreviewDims('cfdPreviewDims', readSize(cfdSelect, cfdCustom));
      if (cfdSelect.value !== 'custom') cfdHandler(readSize(cfdSelect, cfdCustom));
    });

    [document.getElementById('kdsCustomW'), document.getElementById('kdsCustomH')].forEach(input => {
      input.addEventListener('change', () => {
        this._updatePreviewDims('kdsPreviewDims', readSize(kdsSelect, kdsCustom));
        kdsHandler(readSize(kdsSelect, kdsCustom));
      });
    });

    [document.getElementById('cfdCustomW'), document.getElementById('cfdCustomH')].forEach(input => {
      input.addEventListener('change', () => {
        this._updatePreviewDims('cfdPreviewDims', readSize(cfdSelect, cfdCustom));
        cfdHandler(readSize(cfdSelect, cfdCustom));
      });
    });
  }

  // ── CFD ad image ──────────────────────────────────────────────────────────────

  _addHandlerCFDAdUpload(handler) {
    const input      = document.getElementById('cfdAdInput');
    const fileNameEl = document.getElementById('cfdAdFileName');
    input.addEventListener('change', () => {
      const file = input.files[0];
      if (!file) return;
      fileNameEl.textContent = file.name;
      handler(file);
    });
  }

  showCFDAdPreview(url) {
    this._syncImagePreview('cfdAd', url);
  }

  _addHandlerCFDAdRemove(handler) {
    document.getElementById('cfdAdRemoveBtn').addEventListener('click', () => {
      document.getElementById('cfdAdFileName').textContent = 'No file chosen';
      document.getElementById('cfdAdInput').value = '';
      handler();
    });
  }

  // ── Pickup screen ad image ──────────────────────────────────────────────────

  _addHandlerPickupAdUpload(handler) {
    const input      = document.getElementById('pickupAdInput');
    const fileNameEl = document.getElementById('pickupAdFileName');
    input.addEventListener('change', () => {
      const file = input.files[0];
      if (!file) return;
      fileNameEl.textContent = file.name;
      handler(file);
    });
  }

  showPickupAdPreview(url) {
    this._syncImagePreview('pickupAd', url);
  }

  _addHandlerPickupAdRemove(handler) {
    document.getElementById('pickupAdRemoveBtn').addEventListener('click', () => {
      document.getElementById('pickupAdFileName').textContent = 'No file chosen';
      document.getElementById('pickupAdInput').value = '';
      handler();
    });
  }

  // ── CFD payment QR image ────────────────────────────────────────────────────

  _addHandlerCFDQRUpload(handler) {
    const input      = document.getElementById('cfdQrInput');
    const fileNameEl = document.getElementById('cfdQrFileName');
    input.addEventListener('change', () => {
      const file = input.files[0];
      if (!file) return;
      fileNameEl.textContent = file.name;
      handler(file);
    });
  }

  showCFDQRPreview(url) {
    this._syncImagePreview('cfdQr', url);
  }

  _addHandlerCFDQRRemove(handler) {
    document.getElementById('cfdQrRemoveBtn').addEventListener('click', () => {
      document.getElementById('cfdQrFileName').textContent = 'No file chosen';
      document.getElementById('cfdQrInput').value = '';
      handler();
    });
  }

  // Thresholds are stored in seconds; the UI shows them as minute : second.
  syncKDSThresholds(yellow, red, auto) {
    const set = (minEl, secEl, total) => {
      const t = Math.max(0, parseInt(total) || 0);
      if (minEl) minEl.value = Math.floor(t / 60);
      if (secEl) secEl.value = String(t % 60).padStart(2, '0');
    };
    set(this._kdsYellowMin, this._kdsYellowSec, yellow);
    set(this._kdsRedMin, this._kdsRedSec, red);
    set(this._kdsAutoMin, this._kdsAutoSec, auto);
    this._renderKDSLifecycle(yellow, red, auto);
  }

  _formatMinSec(totalSeconds) {
    const t = Math.max(0, parseInt(totalSeconds) || 0);
    return `${Math.floor(t / 60)}:${String(t % 60).padStart(2, '0')}`;
  }

  // Keeps the lifecycle diagram + the colored pills in the Timer settings list
  // in sync with the current threshold values (initial load and live edits).
  _renderKDSLifecycle(yellow, red, auto) {
    const y = this._formatMinSec(yellow);
    const r = this._formatMinSec(red);
    const a = this._formatMinSec(auto);
    const setText = (id, text) => { const el = document.getElementById(id); if (el) el.textContent = text; };
    setText('kdsLifecycleYellowTime', y);
    setText('kdsLifecycleRedTime', r);
    setText('kdsLifecycleAutoTime', a);
    setText('kdsPillYellow', y);
    setText('kdsPillRed', r);
    setText('kdsPillAuto', a);
  }

  _addHandlerKDSThresholds(handler) {
    // minute:second -> seconds; fall back to the new-business defaults if a pair
    // reads zero/blank so a timer is never accidentally disabled.
    const toSecs = (minEl, secEl, fallback) => {
      const m = Math.max(0, parseInt(minEl?.value) || 0);
      const s = Math.min(59, Math.max(0, parseInt(secEl?.value) || 0));
      const total = m * 60 + s;
      return total > 0 ? total : fallback;
    };
    [this._kdsYellowMin, this._kdsYellowSec, this._kdsRedMin,
     this._kdsRedSec, this._kdsAutoMin, this._kdsAutoSec].forEach(input => {
      input?.addEventListener('change', () => {
        const values = {
          yellow: toSecs(this._kdsYellowMin, this._kdsYellowSec, 600),
          red:    toSecs(this._kdsRedMin, this._kdsRedSec, 900),
          auto:   toSecs(this._kdsAutoMin, this._kdsAutoSec, 3600),
        };
        this._renderKDSLifecycle(values.yellow, values.red, values.auto);
        handler(values);
      });
    });
  }
}

export default new SettingsView();
